import { useState } from "react";
import { act as ccAct } from "@/lib/ccApi";
import { useBoard } from "@/hooks/useBoard";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";

// The project workshop (Jeff, 2026-10-21): the Projects page is where ideas get
// crafted into real projects. Jeff dumps raw thoughts — Kit works them into a
// right-sized goal (proposal lands on the card) — then phases (1–8, split guard),
// definition of done, skills, resources. Two columns (Jeff, same day): his side
// on the left, Kit's side on the right — no full-width slabs.

const IDEAS_HINT = "Raw ideas welcome — nothing here is final until you shaped it into a project.";

const CraftRow = ({ item, board, onFlash, desktop }) => {
  const p = item.payload || {};
  const prop = p.kit_goal_proposal || null;
  const [goal, setGoal] = useState(prop?.goal || "");
  const [thoughts, setThoughts] = useState(p.jeff?.notes?.map((n) => n.note).join("\n\n") || "");
  const [phases, setPhases] = useState(prop?.phases_sketch || []);
  const [dod, setDod] = useState("");
  const [skills, setSkills] = useState(prop?.skill_suggestions?.join(", ") || "");
  const [resources, setResources] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("goal"); // goal → shape → promoted

  const refines = (p.jeff?.notes || []).length;
  const filled = phases.map((x) => x.trim()).filter(Boolean);
  const tooBig = filled.length > 8;

  // Jeff 2026-10-21: Jeff gives raw thoughts and context; Kit arrives at the
  // good goal. Saving thoughts files them on the idea and pings Kit's feed;
  // the goal proposal comes back from Kit onto this card.
  const shareThoughts = async () => {
    setBusy(true);
    try {
      const ok = await board.act("note", item.slug, thoughts.trim());
      if (ok) onFlash("Saved — Kit picks this up and brings a goal proposal back onto the card.");
    } finally { setBusy(false); }
  };

  const useProposal = () => {
    setGoal(prop.goal);
    if (prop.phases_sketch?.length) setPhases(prop.phases_sketch);
    setStep("shape");
  };

  const promote = async () => {
    setBusy(true);
    try {
      const res = await ccAct({
        verb: "promoteIdea",
        board: "goals",
        target: item.slug,
        value: goal.trim(),
        craft: {
          goal: goal.trim(),
          phases: filled,
          definition_of_done: dod.trim(),
          skills: skills.split(",").map((x) => x.trim()).filter(Boolean),
          resources: resources.split(",").map((x) => x.trim()).filter(Boolean),
        },
      });
      if (res) {
        setStep("promoted");
        onFlash("It's a real project now — shaped, defined, and ready to run. It lives below.");
      }
    } finally { setBusy(false); }
  };

  const cols = desktop ? "grid grid-cols-2 gap-5" : "flex flex-col gap-5";

  return (
    <div className="rounded-card border border-os-border p-5" data-testid={`craft-${item.slug}`}>
      {step === "promoted" ? (
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-[0.9375rem] font-semibold text-os-ink">{goal}</p>
          <StateWord tone="green">Project created</StateWord>
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-3">
            <p className="min-w-0 truncate text-[0.9375rem] font-semibold text-os-ink">{p.title}</p>
            <StateWord tone="muted">{refines > 0 ? `${refines} refinement${refines === 1 ? "" : "s"}` : "Idea"}</StateWord>
          </div>
          {step === "shape" && goal.trim() && (
            <p className="mt-1 truncate text-[0.8125rem] text-os-ink-2">Shaping: {goal}</p>
          )}
          <div className={`${cols} mt-4`}>
            {/* Jeff's side — what he gives */}
            <div className="flex flex-col gap-2" data-testid={`craft-jeff-side-${item.slug}`}>
              {step === "goal" ? (
                <>
                  <Label as="h3">Your starting point</Label>
                  <p className="text-[0.8125rem] leading-[1.5] text-os-ink-3">
                    Talk it out — thoughts, context, why it matters. Untidy is fine; shaping is Kit's job, not yours.
                  </p>
                  <textarea
                    value={thoughts}
                    onChange={(e) => setThoughts(e.target.value)}
                    rows={6}
                    data-testid={`craft-goal-${item.slug}`}
                    className="w-full resize-none rounded-md border border-os-border bg-white px-3 py-2 text-[0.875rem] leading-[1.55] text-os-ink outline-none focus:border-os-border-strong"
                    placeholder="What's the idea? What triggered it? What does winning look like?"
                  />
                  <div>
                    <button type="button" onClick={shareThoughts} disabled={busy || !thoughts.trim()}
                      className="rounded-md border border-os-border-strong bg-os-ink px-3 py-1.5 font-mono text-[0.71875rem] text-white hover:opacity-90 disabled:opacity-50"
                      data-testid={`idea-refine-${item.slug}`}>
                      Save & hand to Kit
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Label as="h3">Phases — 1 to 8, one per line</Label>
                  <textarea
                    value={phases.join("\n")}
                    onChange={(e) => setPhases(e.target.value.split("\n"))}
                    rows={6}
                    data-testid={`craft-phases-${item.slug}`}
                    className="w-full resize-none rounded-md border border-os-border bg-white px-3 py-2 text-[0.8125rem] leading-[1.55] text-os-ink outline-none focus:border-os-border-strong"
                    placeholder={"One phase per line, e.g.\nWire the email trigger\nOpen an audit project on intake\nRun the audit skill set"}
                  />
                  {filled.length > 0 && (
                    <MonoMeta as="p">
                      {filled.length} phase{filled.length === 1 ? "" : "s"}
                      {tooBig ? " — too broad, split into multiple projects" : " — right-sized"}
                    </MonoMeta>
                  )}
                </>
              )}
            </div>

            {/* Kit's side — what comes back */}
            <div className="flex flex-col gap-2" data-testid={`craft-kit-side-${item.slug}`}>
              {step === "goal" ? (
                prop ? (
                  <>
                    <Label as="h3">Kit's goal proposal</Label>
                    <div className="rounded-md border border-os-border bg-[var(--os-parchment,#faf7f2)] px-3 py-2.5" data-testid={`goal-proposal-${item.slug}`}>
                      <p className="text-[0.9375rem] font-semibold leading-[1.5] text-os-ink">{prop.goal}</p>
                      {prop.why_fit && <p className="mt-1.5 text-[0.8125rem] leading-[1.5] text-os-ink-2">{prop.why_fit}</p>}
                      {prop.phases_sketch?.length > 0 && (
                        <p className="mt-1.5 text-[0.75rem] text-os-ink-3">
                          Rough shape: {prop.phases_sketch.length} phase{prop.phases_sketch.length === 1 ? "" : "s"} — pre-filled on the left, adjust freely.
                        </p>
                      )}
                      {prop.questions?.length > 0 && (
                        <div className="mt-2">
                          <p className="font-mono text-[0.625rem] uppercase tracking-wide text-os-ink-3">Open questions</p>
                          <ul className="mt-1 flex flex-col gap-0.5">
                            {prop.questions.map((qq) => <li key={qq} className="text-[0.8125rem] leading-[1.5] text-os-ink-2">— {qq}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={useProposal} disabled={busy}
                        className="rounded-md border border-os-border-strong bg-os-ink px-3 py-1.5 font-mono text-[0.71875rem] text-white hover:opacity-90 disabled:opacity-50"
                        data-testid={`idea-shape-${item.slug}`}>
                        Use Kit's proposed goal →
                      </button>
                      <button type="button" onClick={() => { setStep("shape"); }} disabled={busy}
                        className="font-mono text-[0.6875rem] text-os-ink-3 underline underline-offset-2 hover:text-os-ink">
                        edit wording first
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <Label as="h3">Kit's side</Label>
                    <Card quiet className="px-4 py-3.5">
                      <p className="text-[0.8125rem] leading-[1.5] text-os-ink-2">
                        Save your thoughts and Kit works them into a right-sized goal — the proposal lands right here with a rough phase sketch and any open questions.
                      </p>
                    </Card>
                    {thoughts.trim() && (
                      <button type="button" onClick={() => { setGoal(p.title); setStep("shape"); }}
                        className="self-start font-mono text-[0.6875rem] text-os-ink-3 underline underline-offset-2 hover:text-os-ink"
                        data-testid={`idea-shape-${item.slug}`}>
                        shape it myself now
                      </button>
                    )}
                  </>
                )
              ) : (
                <>
                  <Label as="h3">Definition of done</Label>
                  <input
                    value={dod}
                    onChange={(e) => setDod(e.target.value)}
                    data-testid={`craft-dod-${item.slug}`}
                    className="w-full rounded-md border border-os-border bg-white px-3 py-2 text-[0.875rem] text-os-ink outline-none focus:border-os-border-strong"
                    placeholder="The project is done when…"
                  />
                  <Label as="h3">Skills Kit will draw on</Label>
                  <input
                    value={skills}
                    onChange={(e) => setSkills(e.target.value)}
                    data-testid={`craft-skills-${item.slug}`}
                    className="w-full rounded-md border border-os-border bg-white px-3 py-2 text-[0.8125rem] text-os-ink outline-none focus:border-os-border-strong"
                    placeholder="railway-deploy, siteguru-audit"
                  />
                  <Label as="h3">Resources in play</Label>
                  <input
                    value={resources}
                    onChange={(e) => setResources(e.target.value)}
                    data-testid={`craft-resources-${item.slug}`}
                    className="w-full rounded-md border border-os-border bg-white px-3 py-2 text-[0.8125rem] text-os-ink outline-none focus:border-os-border-strong"
                    placeholder="Railway volume, Mongo backup"
                  />
                  <div className="mt-1 flex items-center gap-3">
                    <button type="button" onClick={promote} disabled={busy || !goal.trim() || !dod.trim() || tooBig}
                      className="rounded-md border border-os-border-strong bg-os-ink px-3 py-1.5 font-mono text-[0.71875rem] text-white hover:opacity-90 disabled:opacity-50"
                      data-testid={`idea-promote-${item.slug}`}>
                      Make it a project
                    </button>
                    {!dod.trim() && <span className="text-[0.75rem] text-os-ink-3">needs a definition of done</span>}
                    {tooBig && <span className="text-[0.75rem] text-os-red">too broad — 8 max</span>}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const ProjectWorkshop = ({ flash, desktop = false }) => {
  const board = useBoard("goals");
  const ideas = (board.items || []).filter((r) => r.kind === "idea" && r.state !== "ARCHIVED");

  return (
    <section data-testid="project-workshop" className="flex flex-col gap-3">
      <Label as="h2">The project workshop — shape an idea into a project</Label>
      <p className="text-[0.8125rem] text-os-ink-3">{IDEAS_HINT}</p>
      {ideas.length === 0 ? (
        <Card quiet className="px-5 py-4">
          <p className="text-[0.875rem] text-os-ink-2">No raw ideas waiting — dump them on Home under "Get ideas out" and they land here to shape.</p>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {ideas.map((r) => <CraftRow key={r.slug} item={r} board={board} onFlash={flash} desktop={desktop} />)}
        </div>
      )}
    </section>
  );
};

export default ProjectWorkshop;